import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AppContext } from '@plys/src/application/app.context';
import { CartState } from '@plys/src/application/states/cart.state';
import { CheckoutState } from '@plys/src/application/states/checkout.state';
import { ShowState } from '@plys/src/application/states/show.state';
import { StateService } from '@plys/src/infrastructure/state-service.service';
import { hasConfirmedAge, isAgeRestricted, rememberAgeConfirmed, requiredAge } from '@plys/src/models/show';
import { CartPreviewComponent } from '@plys/src/ui/cart-preview/cart-preview.component';
import { CheckoutUIService } from '@plys/src/ui/checkout/checkout-u-i.service';
import { ConfirmDialogComponent } from '@plys/src/ui/confirm-dialog/confirm-dialog.component';
import { MobileCartFooterComponent } from '@plys/src/ui/mobile-cart-footer/mobile-cart-footer.component';
import { MovieWidgetComponent } from '@plys/src/ui/movie-widget/movie-widget.component';
import { ResizePipe } from '@plys/src/ui/media/resize.pipe';
import { ConfiguredItemsTicket } from '@plys/src/ui/configured-items/configured-items.component';
import { TicketExtrasComponent } from '@plys/src/ui/ticket-extras/ticket-extras.component';
import { FilmExtrasComponent } from '@plys/src/ui/film-extras/film-extras.component';
import {
  RemoveTicketDialogComponent,
  RemoveTicketDialogData,
  RemoveTicketOption,
} from '@plys/src/ui/remove-ticket-dialog/remove-ticket-dialog.component';
import { extrasOf } from '@plys/src/models/cart-item';
import { ProductOptionsService } from '@plys/src/ui/product-options-dialog/product-options.service';
import { QuantityControlComponent } from '@plys/src/ui/quantity-control/quantity-control.component';
import { InlineSVGModule } from '@digilize/shared/ui/inline-svg/src';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AvailabilitySnapshot,
  availabilitySnapshot,
  groupProducts,
  needsChoices,
  Product,
  ProductType,
  Supplement,
  TicketAvailabilityState,
  ticketAvailabilityStates,
} from '../../models/product';
import { Ticket } from '../../models/ticket';
import { LocaleCurrencyPipe } from '../../ui/locale-currency.pipe';
import { joinSurcharges } from './surcharge-line';

export interface ProductViewData {
  product: Product;
  supplements: Supplement[];
  /** Decides whether the quantity counter is rendered at all, and whether + is stopped. */
  availabilityState: TicketAvailabilityState;
}

export interface GroupViewData {
  title: string;
  items: ProductViewData[];
}

@Component({
  imports: [
    LocaleCurrencyPipe,
    CommonModule,
    TranslocoModule,
    InlineSVGModule,
    NgOptimizedImage,
    MatProgressSpinnerModule,
    CartPreviewComponent,
    MobileCartFooterComponent,
    MovieWidgetComponent,
    QuantityControlComponent,
    TicketExtrasComponent,
    FilmExtrasComponent,
    ResizePipe,
  ],
  templateUrl: './tickets-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tickets-page.component.scss', '../../styles/list-item.scss'],
})
export class TicketsPageComponent implements OnInit, OnDestroy {
  /** What was left when last read, with the cart's lines at that moment; see `ticketAvailabilityStates`. */
  private readonly snapshot$ = new BehaviorSubject<AvailabilitySnapshot | null>(null);
  readonly ticketProducts$ = this.getProductsWithSupplements();
  protected loading = true;
  private destroy$ = new Subject<void>();
  private ProductType = ProductType;
  protected readonly reserving$ = this.cartState.reserving$;
  private readonly askingSize$ = new BehaviorSubject(false);
  /** The counter's spinner only resets on a true → false change, so a closed size dialog must make one. */
  protected readonly ticketCounterLoading$ = combineLatest([this.reserving$, this.askingSize$]).pipe(
    map(([reserving, asking]) => reserving || asking)
  );
  /** The film's and its format's extras set to the ticket page, once a ticket is in the cart (PAUL-3884). */
  protected readonly filmExtras$ = combineLatest([
    this.showState.ticketPageFilmSupplements$.pipe(startWith([])),
    this.cartState.tickets$,
  ]).pipe(map(([extras, tickets]) => (tickets.length ? extras : [])));
  readonly expandDetails: Record<string, boolean> = {};
  private readonly ticketsFor = new Map<string, ConfiguredItemsTicket>();
  private readonly currency = new LocaleCurrencyPipe(this.transloco);

  constructor(
    public showState: ShowState,
    private cartState: CartState,
    private checkoutState: CheckoutState,
    private checkout: CheckoutUIService,
    private stateService: StateService,
    private appContext: AppContext,
    private dialog: MatDialog,
    private transloco: TranslocoService,
    private productOptions: ProductOptionsService
  ) {}
  ngOnInit(): void {
    /* we are not waiting for response to show products faster*/
    this.showState.fetchSupplements(this.appContext.productIds).subscribe();
    this.readAvailability();
  }

  /** "Incl. 3D toeslag (€ 2,00), XL film toeslag (€ 1,00), en Transactiekosten (€ 0,50)" (PAUL-3932). */
  protected surchargeLine(product: Product): string {
    const parts = product.shownSurcharges.map((surcharge) =>
      this.transloco.translate('tickets.surcharge', {
        name: surcharge.name,
        amount: this.currency.transform(surcharge.amount, product.currency),
      })
    );
    const surcharges = joinSurcharges(
      parts,
      this.transloco.translate('tickets.surcharge_and'),
      this.transloco.translate('tickets.surcharge_and_last')
    );
    return this.transloco.translate('tickets.surcharge_line', { surcharges });
  }

  /**
   * The product list's availability was read when the checkout opened, maybe several steps and cart
   * changes ago, so it is paired with the cart now and read again. A read that crosses an add or a
   * remove cannot be paired with either side of it, so it is dropped and the earlier pairing stays.
   */
  private readAvailability(): void {
    combineLatest([this.showState.products$, this.cartState.tickets$])
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(([products, tickets]) =>
        this.snapshot$.next(
          availabilitySnapshot(new Map(products.map((p) => [p.id, p.availability])), heldProductIds(tickets))
        )
      );

    this.cartState.tickets$
      .pipe(
        first(),
        switchMap((before) =>
          this.showState.fetchAvailability().pipe(
            concatMap((availability) =>
              this.cartState.tickets$.pipe(
                first(),
                filter((after) => sameLines(heldProductIds(before), heldProductIds(after))),
                map((after) => availabilitySnapshot(availability, heldProductIds(after)))
              )
            )
          )
        ),
        catchError(() => EMPTY),
        takeUntil(this.destroy$)
      )
      .subscribe((snapshot) => this.snapshot$.next(snapshot));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addTicketToCart(product: Product): void {
    this.addTicket(product).pipe(takeUntil(this.destroy$)).subscribe();
  }

  /** Kept per ticket product, so the rows under it get the same input on every change detection. */
  ticketFor(product: Product): ConfiguredItemsTicket {
    if (!this.ticketsFor.has(product.id)) {
      this.ticketsFor.set(product.id, { name: product.name, add: () => this.addTicket(product) });
    }
    return this.ticketsFor.get(product.id);
  }

  /** Emits once the ticket is in the cart; nothing when the buyer closes a dialog asking its choices. */
  private addTicket(product: Product): Observable<unknown> {
    // No sold-out guard here: + is disabled for anything the availability snapshot rules out, and a
    // race against another buyer is caught by the reservation call itself.
    // A required size or deal is added with the ticket, so it is asked first.
    if (needsChoices(product)) this.askingSize$.next(true);
    return this.productOptions.askTicketChoices(product).pipe(
      finalize(() => this.askingSize$.next(false)),
      take(1),
      concatMap((choices) => this.cartState.addTicketProduct(product, choices))
    );
  }

  /**
   * Removing a ticket removes the extras hanging under it, so when the product's tickets carry different
   * ones the buyer picks which ticket goes; when they all carry the same, any one does.
   */
  removeTicketFromCart(id: string): void {
    combineLatest([this.cartState.tickets$, this.cartState.items$, this.cartState.combiDeals$])
      .pipe(
        first(),
        switchMap(([tickets, items, deals]) => {
          const options: RemoveTicketOption[] = tickets
            .filter((ticket) => ticket.product?.id === id && ticket.reservationId)
            .map((ticket, index) => ({
              reservationId: ticket.reservationId,
              number: index + 1,
              extras: extrasOf(ticket.reservationId, items, deals),
            }));
          const kinds = new Set(options.map((option) => [...option.extras].sort().join('\n')));
          if (kinds.size <= 1) return of(null);
          return this.dialog
            .open<RemoveTicketDialogComponent, RemoveTicketDialogData, string>(RemoveTicketDialogComponent, {
              width: '600px',
              maxWidth: '95vw',
              autoFocus: false,
              data: { title: tickets.find((ticket) => ticket.product?.id === id)?.product?.name ?? '', options },
            })
            .afterClosed()
            .pipe(filter((reservationId): reservationId is string => !!reservationId));
        }),
        concatMap((reservationId) => this.cartState.removeTicketByProductId(id, reservationId)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  getTicketQuantity(productId: string): Observable<number> {
    return this.cartState.getTicketQuantity(productId);
  }

  getProductsWithSupplements(): Observable<GroupViewData[]> {
    return combineLatest([
      this.showState.products$,
      this.showState.ticketPageSupplements$.pipe(startWith([])),
      this.cartState.tickets$,
      this.snapshot$.pipe(filter((snapshot) => !!snapshot)),
    ]).pipe(
      map(([products, supplements, tickets, snapshot]) => {
        const states = ticketAvailabilityStates(
          products.map((product) => product.id),
          snapshot,
          heldProductIds(tickets)
        );
        return groupProducts(products).map((group) => ({
          title: group.title,
          items: group.products.map((product) => ({
            product,
            supplements: supplements.filter((supplement) => product.id === supplement.mainProductId),
            availabilityState: states.get(product.id),
          })),
        }));
      }),
      tap((products) => (this.loading = !products))
    );
  }

  hasItemsWithin(productId: string, supplements: Supplement[]): Observable<boolean> {
    return this.getTicketQuantity(productId).pipe(
      map((quantity) => quantity > 0),
      map((hasProductInCart) => hasProductInCart && supplements?.length > 0)
    );
  }

  public handleContinueClick(): void {
    this.confirmAgeIfRequired()
      .pipe(
        filter((proceed) => proceed),
        switchMap(() => this.cartState.requireReservation()),
        first(),
        tap((require) => {
          if (require) this.checkoutState.moveReservingStepAsNextStep(this.cartState.getCartIdValue());
        }),
        switchMap(() => this.stateService.tryToSkipReservingStep()),
        tap(() => {
          this.checkout.goNext(false);
        })
      )
      .subscribe();
  }

  /* Asked once per browser session so every order is backed by a confirmation from that buying session.
     The message names the movie's own rating, but the age the buyer confirms stays 16 for every rating:
     16 is the legally binding minimum, so 16-year-olds may see an 18-rated movie. */
  private confirmAgeIfRequired(): Observable<boolean> {
    const show = this.showState.show;
    if (!isAgeRestricted(show) || hasConfirmedAge()) return of(true);

    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.transloco.translate('tickets.age_verification.title'),
          message: this.transloco.translate('tickets.age_verification.message', { age: requiredAge(show) }),
          confirmText: this.transloco.translate('tickets.age_verification.confirm'),
        },
      })
      .afterClosed()
      .pipe(
        map((result) => result === true),
        tap((confirmed) => confirmed && rememberAgeConfirmed())
      );
  }

  public toggleDetails(productId: string, supplementId: string = ''): void {
    this.expandDetails[productId + supplementId] = !this.expandDetails[productId + supplementId];
  }

  public isExpanded(productId: string, supplementId: string = ''): boolean {
    return this.expandDetails[productId + supplementId] || false;
  }

  public isMenuVisible(item: Product, supplements?: Supplement[]): boolean {
    return !supplements?.length && item.hasReservation;
  }

  get disabledButton$(): Observable<boolean> {
    return combineLatest([this.cartState.tickets$, this.reserving$]).pipe(
      map(([tickets, reserving]) => tickets?.length === 0 || reserving)
    );
  }
}

function heldProductIds(tickets: Ticket[]): string[] {
  return tickets.map((ticket) => ticket.product?.id).filter((id): id is string => !!id);
}

function sameLines(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}
